import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const DocumentUploadSection = ({
  title,
  description,
  acceptedFormats,
  maxSize,
  file,
  onFileSelect,
  onFileRemove,
  error,
  isRequired = true,
  allowCamera = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelection(droppedFiles[0]);
    }
  };

  const handleFileSelection = async (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    if (!acceptedFormats.includes(fileExtension)) {
      onFileSelect(
        null,
        `Please select a file in one of these formats: ${acceptedFormats.join(
          ", "
        )}`
      );
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSize * 1024 * 1024) {
      onFileSelect(null, `File size must be less than ${maxSize}MB`);
      return;
    }

    setIsUploading(true);

    onFileSelect(selectedFile, null);
    setIsUploading(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileSelection(selectedFile);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "uploaded":
        return (
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
        );
      case "processing":
        return <Icon name="Clock" size={20} color="var(--color-warning)" />;
      case "rejected":
        return <Icon name="XCircle" size={20} color="var(--color-error)" />;
      default:
        return (
          <Icon name="File" size={20} color="var(--color-text-secondary)" />
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      {/* Section Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            {isRequired && <span className="text-error text-sm">*</span>}
          </div>
          <p className="text-sm text-text-secondary mt-1">{description}</p>
        </div>
        {file && (
          <div className="flex items-center space-x-1">
            {getStatusIcon(file.status)}
            <span className="text-sm font-medium text-success">Uploaded</span>
          </div>
        )}
      </div>

      {/* Upload Area or File Preview */}
      {!file ? (
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-smooth cursor-pointer
            ${
              isDragOver
                ? "border-primary bg-primary/5"
                : error
                ? "border-error bg-error/5"
                : "border-border hover:border-primary hover:bg-primary/5"
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          {isUploading ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="animate-spin">
                  <Icon name="Loader2" size={32} color="var(--color-primary)" />
                </div>
              </div>
              <p className="text-sm font-medium text-primary">Uploading...</p>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full animate-pulse"
                  style={{ width: "60%" }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Upload" size={24} color="var(--color-primary)" />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Drag and drop your file here, or click to browse
                </p>
                <p className="text-xs text-text-secondary">
                  Supported formats: {acceptedFormats.join(", ")} • Max size:{" "}
                  {maxSize}MB
                </p>
              </div>

              <div className="flex items-center justify-center space-x-4">
                <Button variant="outline" size="sm" onClick={handleBrowseClick}>
                  <Icon name="FolderOpen" size={16} className="mr-2" />
                  Browse Files
                </Button>

                {allowCamera && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCameraClick}
                  >
                    <Icon name="Camera" size={16} className="mr-2" />
                    Take Photo
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <div className="flex items-start space-x-4">
            {/* File Preview */}
            <div className="flex-shrink-0">
              {file.type?.startsWith("image/") ? (
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                  <img
                    src={file.url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon
                    name="FileText"
                    size={24}
                    color="var(--color-primary)"
                  />
                </div>
              )}
            </div>

            {/* File Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    {formatFileSize(file.size)} • Uploaded{" "}
                    {file.uploadedAt?.toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBrowseClick}
                  >
                    Replace
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onFileRemove()}
                  >
                    <Icon name="Trash2" size={16} color="var(--color-error)" />
                  </Button>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center space-x-2 mt-2">
                {getStatusIcon(file.status)}
                <span
                  className={`text-xs font-medium ${
                    file.status === "uploaded"
                      ? "text-success"
                      : file.status === "processing"
                      ? "text-warning"
                      : file.status === "rejected"
                      ? "text-error"
                      : "text-text-secondary"
                  }`}
                >
                  {file.status === "uploaded"
                    ? "Successfully uploaded"
                    : file.status === "processing"
                    ? "Processing..."
                    : file.status === "rejected"
                    ? "Rejected - Please reupload"
                    : "Unknown status"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-md">
          <Icon
            name="AlertCircle"
            size={16}
            color="var(--color-error)"
            className="mt-0.5 flex-shrink-0"
          />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.map((format) => `.${format}`).join(",")}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {allowCamera && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileInputChange}
          className="hidden"
        />
      )}
    </div>
  );
};

export default DocumentUploadSection;
